import { useEffect, useRef, useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import { useAuth } from '@/context/AuthContext';
import { ApiError, sendSmsCode } from '@/services/authApi';

const PHONE_RE = /^1[3-9]\d{9}$/;

export default function LoginModal() {
  const { loginOpen, closeLogin, signInWithSms } = useAuth();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [agreed, setAgreed] = useState(false);
  /**
   * 协议确认子弹窗。
   *
   * 交互约定（区别于常见的报错文案）：**未勾选协议时不提示错误**，直接弹出
   * 协议内容让用户阅读，点「同意并继续」即视为勾选并**自动续接**刚才的动作
   * （获取验证码 / 登录），不打断流程。
   */
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementNext, setAgreementNext] = useState<'send' | 'verify' | null>(null);

  /** 未勾选协议时打开协议弹窗并记住待续接动作；返回 false 表示已拦截。 */
  const requireAgreement = (next: 'send' | 'verify'): boolean => {
    if (agreed) return true;
    setAgreementNext(next);
    setAgreementOpen(true);
    return false;
  };
  const [error, setError] = useState('');
  const [hint, setHint] = useState('');
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!loginOpen) {
      setCode('');
      setError('');
      setHint('');
      setCountdown(0);
      setSending(false);
      setSubmitting(false);
      setCodeSent(false);
    }
  }, [loginOpen]);

  useEffect(() => {
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, []);

  const startCountdown = (seconds: number) => {
    setCountdown(seconds);
    if (timer.current) window.clearInterval(timer.current);
    timer.current = window.setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          if (timer.current) window.clearInterval(timer.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const validPhone = PHONE_RE.test(phone);

  const handleSend = async () => {
    if (!validPhone) {
      setError('请输入正确的中国大陆 11 位手机号');
      return;
    }
    if (!requireAgreement('send')) return;
    await doSend();
  };

  const handleVerify = async () => {
    if (!validPhone) {
      setError('请输入正确的中国大陆 11 位手机号');
      return;
    }
    if (!requireAgreement('verify')) return;
    await doVerify();
  };

  const doSend = async () => {
    setError('');
    setHint('');
    setSending(true);
    try {
      const result = await sendSmsCode(phone);
      setCodeSent(true);
      startCountdown(result.resendAfter || 60);
      setHint('验证码已发送，请查收短信。');
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : '验证码发送失败，请稍后重试';
      setError(msg);
      setCodeSent(false);
    } finally {
      setSending(false);
    }
  };

  const doVerify = async () => {
    if (!code || code.length !== 6) {
      setError('请输入 6 位验证码');
      return;
    }
    if (!codeSent) {
      setError('请先获取验证码');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await signInWithSms(phone, code);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : '登录失败，请稍后重试';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <Modal open={loginOpen} onClose={closeLogin} width="max-w-md" title="登录 / 注册 TaxInsight">
      <div className="space-y-4">
        <p className="text-xs text-foreground-500">
          使用手机号验证码登录，不跳转独立页面。首次验证成功将自动开户。
        </p>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-foreground-700" htmlFor="login-phone">
            手机号
          </label>
          <div className="flex items-center gap-2 rounded-lg border border-background-300 bg-background-50 px-3 focus-within:border-primary-400">
            <i className="ri-smartphone-line text-base text-foreground-400" aria-hidden />
            <input
              id="login-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
              inputMode="numeric"
              autoComplete="tel"
              placeholder="请输入 11 位手机号"
              className="h-10 w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-foreground-700" htmlFor="login-code">
            验证码
          </label>
          <div className="flex items-center gap-2">
            <div className="flex flex-1 items-center gap-2 rounded-lg border border-background-300 bg-background-50 px-3 focus-within:border-primary-400">
              <i className="ri-shield-keyhole-line text-base text-foreground-400" aria-hidden />
              <input
                id="login-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6 位验证码"
                className="h-10 w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={sending || countdown > 0}
              className="h-10 w-28 shrink-0 rounded-lg border border-background-300 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            >
              {sending ? '发送中…' : countdown > 0 ? `${countdown}s` : codeSent ? '重新获取' : '获取验证码'}
            </button>
          </div>
          {hint ? (
            <p className="mt-1.5 text-xs text-primary-600">
              <i className="ri-information-line mr-1" aria-hidden />
              {hint}
            </p>
          ) : null}
        </div>

        <label className="flex cursor-pointer items-start gap-2 text-xs text-foreground-600">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-3.5 w-3.5 accent-primary-500"
          />
          <span>
            我已阅读并同意
            <span className="mx-1 text-primary-600 underline">《用户协议》</span>与
            <span className="mx-1 text-primary-600 underline">《隐私政策》</span>
          </span>
        </label>

        {error ? (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
          >
            <i className="ri-error-warning-line mr-1" aria-hidden />
            {error}
          </p>
        ) : null}

        <button
          type="button"
          onClick={() => void handleVerify()}
          disabled={submitting}
          className="flex h-11 w-full items-center justify-center rounded-lg bg-primary-500 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
        >
          {submitting ? '登录中…' : '登录 / 注册'}
        </button>
      </div>
    </Modal>

    {/* 协议确认子弹窗：未勾选协议提交时不报错，而是先阅读、确认后自动续接原动作 */}
    <Modal
      open={agreementOpen}
      onClose={() => setAgreementOpen(false)}
      width="max-w-md"
      title="服务协议与隐私保护"
    >
      <div className="max-h-[46vh] overflow-y-auto space-y-3 text-xs leading-relaxed text-foreground-600">
        <p>
          欢迎使用 TaxInsight 企业税务线索洞察服务。在使用本服务前，请您务必仔细阅读并充分理解
          <span className="font-medium text-foreground-800">《用户协议》</span>与
          <span className="font-medium text-foreground-800">《隐私政策》</span>，
          其中与您权益存在重大关系的条款已用加粗提示。
        </p>
        <p>
          <span className="font-medium text-foreground-800">一、服务内容：</span>
          本服务基于公开信息与您上传的资料，运用模型对企业经营事件、涉税事项与初步线索进行分析与整理，
          分析结果供您参考，不构成税务、法律或投资建议；重要决策请咨询专业机构。
        </p>
        <p>
          <span className="font-medium text-foreground-800">二、账号与费用：</span>
          您使用手机号验证码登录，首次验证成功将自动创建账号。平台按账号额度提供初步分析与深度挖掘服务，
          具体以您所在租户的配额与权益为准。
        </p>
        <p>
          <span className="font-medium text-foreground-800">三、个人信息保护：</span>
          我们仅收集实现服务所必需的信息（手机号、登录企业主体与您主动上传的资料），
          用于身份识别、结果留存与服务改进；不会向任何第三方出售您的个人信息。
          您上传的资料默认仅本账号可见。
        </p>
        <p>
          <span className="font-medium text-foreground-800">四、数据与结果：</span>
          您发起的分析记录与结果快照将保存至「我的分析」，便于随时回看；
          您可以随时删除相关记录。
        </p>
        <p>
          <span className="font-medium text-foreground-800">五、免责声明：</span>
          公开信息与模型输出可能存在滞后或偏差，平台对因信息滞后、模型误差导致的
          直接或间接损失不承担责任。如您不同意上述条款，请停止注册或使用本服务。
        </p>
      </div>
      <div className="mt-5 flex items-center justify-end gap-3">
        <Button variant="ghost" onClick={() => setAgreementOpen(false)}>
          暂不同意
        </Button>
        <Button
          onClick={() => {
            // 勾选 + 关闭 + 续接被拦截的动作（获取验证码 / 登录）
            setAgreed(true);
            setAgreementOpen(false);
            const next = agreementNext;
            setAgreementNext(null);
            if (next === 'send') void doSend();
            else if (next === 'verify') void doVerify();
          }}
        >
          同意并继续
        </Button>
      </div>
    </Modal>
    </>
  );
}
